import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraEyebrow, SpecraInput, SpecraSurface, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, ExternalLink, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { PageBody } from '../components/layout';
import index from '../guides/journeys/index.json';
import { useGuideMemory } from '../guides/memory';
import { useTenantAreas } from '../lib/access/useTenantAreas';
import { startChat } from '../lib/chat/startChat';
// The support site ships per environment as support.<env>.specra.ai, mirroring
// the app's own agents.<env>.specra.ai host. Derive it from the current origin
// the same way Settings/SSOConfigPage derive the auth host, so a new
// environment needs no build-time env var or deploy wiring. Local dev has no
// support site of its own, so it falls back to staging.
const STAGING_SUPPORT_URL = 'https://support.staging.specra.ai';
const getSupportSiteUrl = ()=>{
    const { hostname, origin } = window.location;
    // Domain-based deployments: agents.<env>.specra.ai → support.<env>.specra.ai
    if (hostname.startsWith('agents.') && hostname.endsWith('.specra.ai')) {
        return origin.replace('agents.', 'support.');
    }
    // Port-based/local deployments have no support site — send them to staging.
    return STAGING_SUPPORT_URL;
};
/** What people ask most, in their words. The assistant answers each from the guides. */ export const COMMON_QUESTIONS = [
    "I'm new here. What is this app and where do I start?",
    'How do I submit a deliverable?',
    'Where can I see what is due?',
    'My coworker can’t see a program. How do I give them access?',
    'How do I start tracking a new opportunity?',
    'How do I give you a document to read?'
];
/** One "first day" question per kind of user; each answer is fitted to that role. */ export const FIRST_DAY = [
    'Program Lead',
    'Delivery Ops Coordinator',
    'Quality Assurance',
    'Contracts & Mods',
    'Finance / EAC Controller',
    'Growth Operations',
    'Proposal manager',
    'Administrator'
];
/** Group headings for the guide catalog, in display order. */ export const GUIDE_GROUPS = [
    'Delivery',
    'Proposals',
    'Growth',
    'Registries',
    'Admin',
    'Assistant & documents',
    'Other'
];
/** First path segments (exact match) that belong to each group above. */ const GROUP_PREFIXES = {
    Delivery: [
        '/delivery',
        '/programs',
        '/project'
    ],
    Proposals: [
        '/proposal',
        '/proposals',
        '/rfi-response'
    ],
    Growth: [
        '/growth',
        '/capture',
        '/opportunities',
        '/landscapes'
    ],
    Registries: [
        '/registries'
    ],
    Admin: [
        '/settings',
        '/admin'
    ],
    'Assistant & documents': [
        '/assistant',
        '/library'
    ]
};
/** Which catalog group a guide's page belongs under, by its first path segment. */ export function guideGroup(page) {
    const [firstSegment] = page.split('/').filter(Boolean);
    const segment = `/${firstSegment ?? ''}`;
    for (const group of GUIDE_GROUPS){
        if (GROUP_PREFIXES[group]?.includes(segment)) return group;
    }
    return 'Other';
}
function groupGuides(guides) {
    const byGroup = new Map();
    for (const guide of guides){
        const group = guideGroup(guide.page);
        byGroup.set(group, [
            ...byGroup.get(group) ?? [],
            guide
        ]);
    }
    return GUIDE_GROUPS.filter((group)=>byGroup.has(group)).map((group)=>[
            group,
            byGroup.get(group)
        ]);
}
/**
 * A record-scoped guide's page is a route pattern like
 * `/programs/:programId/risks` — there's no record to start it on from Help.
 * Names the kind of record the reader needs open first: the segment right
 * before the first `:param`, singularised by stripping a trailing "s".
 */ export function recordLabel(page) {
    const segments = page.split('/').filter(Boolean);
    const paramIndex = segments.findIndex((segment)=>segment.startsWith(':'));
    const before = paramIndex === -1 ? segments : segments.slice(0, paramIndex);
    const last = before.at(-1) ?? '';
    return last.endsWith('s') ? last.slice(0, -1) : last;
}
function GuideRow({ done, guide, onStart }) {
    const recordScoped = guide.page.includes(':');
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        className: "space-y-[var(--space-1)] px-[var(--space-3)] py-[var(--space-2)]",
        "data-done": done ? 'true' : undefined,
        "data-guide": guide.name,
        "data-testid": Testids.help.guideItem,
        radius: "md",
        tone: "rail",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start justify-between gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "div",
                        className: "font-medium",
                        size: "body-ui",
                        children: guide.title
                    }),
                    done && /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "span",
                        className: "flex shrink-0 items-center gap-[var(--space-1)]",
                        size: "caption",
                        tone: "muted",
                        children: [
                            /*#__PURE__*/ _jsx(Check, {
                                "aria-hidden": true,
                                className: "h-3 w-3"
                            }),
                            "Done"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                className: "line-clamp-2",
                size: "caption",
                tone: "muted",
                children: guide.doc
            }),
            recordScoped ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                children: `Open a ${recordLabel(guide.page)} first, then choose it from Guide me.`
            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.help.guideStart,
                onClick: onStart,
                size: "sm",
                variant: "secondary",
                children: "Start"
            })
        ]
    });
}
export default function HelpPage() {
    const supportUrl = getSupportSiteUrl();
    const navigate = useNavigate();
    const [question, setQuestion] = useState('');
    const ask = (q)=>{
        const text = q.trim();
        if (text) startChat(navigate, text);
    };
    const onSubmit = (e)=>{
        e.preventDefault();
        ask(question);
    };
    const areas = useTenantAreas();
    const { memory } = useGuideMemory();
    const availableGuides = index.filter((g)=>!g.area || areas[g.area] === true);
    const groupedGuides = groupGuides(availableGuides);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Helmet, {
                children: [
                    /*#__PURE__*/ _jsx("title", {
                        children: "Help - Sprezz"
                    }),
                    /*#__PURE__*/ _jsx("meta", {
                        name: "description",
                        content: "Find answers and get support"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PageBody, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-2xl space-y-[var(--space-6)]",
                    children: [
                        /*#__PURE__*/ _jsxs("form", {
                            className: "space-y-[var(--space-2)]",
                            onSubmit: onSubmit,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    size: "body-ui",
                                    children: "Ask anything about the app, in your own words. You get the steps and a link to the page."
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex gap-[var(--space-2)]",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraInput, {
                                            "aria-label": "Ask a question about the app",
                                            "data-testid": Testids.help.askInput,
                                            onChange: (e)=>setQuestion(e.target.value),
                                            placeholder: "e.g. how do I add someone to my team?",
                                            value: question
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraButton, {
                                            disabled: !question.trim(),
                                            type: "submit",
                                            children: [
                                                /*#__PURE__*/ _jsx(Sparkles, {
                                                    "aria-hidden": "true",
                                                    className: "mr-2 h-4 w-4"
                                                }),
                                                "Ask"
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-[var(--space-2)]",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                    children: "Common questions"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "grid gap-[var(--space-2)] sm:grid-cols-2",
                                    children: COMMON_QUESTIONS.map((q)=>/*#__PURE__*/ _jsx(SpecraSurface, {
                                            className: "px-[var(--space-3)] py-[var(--space-2)]",
                                            "data-testid": Testids.help.question,
                                            interactive: true,
                                            onClick: ()=>ask(q),
                                            radius: "md",
                                            tone: "rail",
                                            children: /*#__PURE__*/ _jsx(SpecraText, {
                                                as: "div",
                                                size: "body-ui",
                                                children: q
                                            })
                                        }, q))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-[var(--space-2)]",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                    children: "Your first day as…"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex flex-wrap gap-[var(--space-2)]",
                                    children: FIRST_DAY.map((role)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                            "data-testid": Testids.help.question,
                                            onClick: ()=>ask(`It's my first day as ${role}. What should I do first?`),
                                            size: "sm",
                                            variant: "secondary",
                                            children: role
                                        }, role))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-[var(--space-3)]",
                            "data-testid": Testids.help.guides,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                    children: "Guides"
                                }),
                                groupedGuides.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    size: "body-ui",
                                    tone: "muted",
                                    children: "No guides are available for your account yet."
                                }) : groupedGuides.map(([group, guides])=>/*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-[var(--space-2)]",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraText, {
                                                as: "p",
                                                className: "font-medium",
                                                size: "caption",
                                                tone: "muted",
                                                children: group
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "space-y-[var(--space-2)]",
                                                children: guides.map((g)=>/*#__PURE__*/ _jsx(GuideRow, {
                                                        done: memory.completed.includes(g.name),
                                                        guide: g,
                                                        onStart: ()=>navigate(`${g.page}?guide=${g.name}`)
                                                    }, g.name))
                                            })
                                        ]
                                    }, group))
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-[var(--space-2)]",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                    children: "More help"
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    size: "body-ui",
                                    tone: "muted",
                                    children: "Every guide, walkthrough and the glossary also live on Sprezz Support."
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    asChild: true,
                                    variant: "secondary",
                                    children: /*#__PURE__*/ _jsxs("a", {
                                        href: supportUrl,
                                        target: "_blank",
                                        rel: "noopener noreferrer",
                                        "data-testid": Testids.help.supportLink,
                                        children: [
                                            /*#__PURE__*/ _jsx(ExternalLink, {
                                                className: "mr-2 h-4 w-4"
                                            }),
                                            "Open Sprezz Support"
                                        ]
                                    })
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
